/** Who a feedback ticket names, against /api/support/reporter. */

import type { SupportReporter } from "@patent-studio/api-types";
import type { Reporter } from "@/lib/support/support-client";
import { BASE_URL, apiFetch, authHeaders } from "@/services/api-client";

/**
 * How long the lookup may take before the ticket goes without it. The send
 * holds the popover open, so a lookup with no bound could hold it as long as
 * the API took to answer.
 */
const LOOKUP_GIVE_UP_AFTER_MS = 5_000;

const NO_CONTACT: Reporter = { hubspotContactId: undefined };

/**
 * Read past `get`, whose refusal handling would end the session or show the
 * no-access screen and take the form off the screen in the middle of a send.
 * The next ordinary request still notices either.
 */
async function readReporter(signal: AbortSignal): Promise<Reporter> {
  const response = await apiFetch(`${BASE_URL}/support/reporter`, {
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) return NO_CONTACT;
  const served = (await response.json()) as SupportReporter;
  return { hubspotContactId: served.hubspot_contact_id ?? undefined };
}

/**
 * The signed-in sender's HubSpot contact, asked for as they send. Never a
 * throw: a ticket that cannot name its sender is still worth sending, so a
 * lookup that fails, or is not answered in time, names no contact.
 */
export async function lookUpReporter(): Promise<Reporter> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const givenUp = new Promise<Reporter>((resolve) => {
    timer = setTimeout(() => {
      resolve(NO_CONTACT);
      controller.abort();
    }, LOOKUP_GIVE_UP_AFTER_MS);
  });
  try {
    return await Promise.race([readReporter(controller.signal), givenUp]);
  } catch {
    // What went wrong is already in the browser's own log of the request.
    return NO_CONTACT;
  } finally {
    clearTimeout(timer);
  }
}
